import { useEffect, useState } from 'react';
import type { MiPerfil, OperacionesClienteFinal } from '../api/ops/clienteFinal';
import type { CondicionesMiCancelacion, ListaMisReservas, MiReserva, OperacionesMisReservas } from '../api/ops/misReservas';
import { ErrorApi } from '../api/problema';
import { Campo } from '../componentes/Campo';
import { Chip, type EstadoChip } from '../componentes/Chip';
import { Fecha } from '../componentes/Fecha';
import { Icono } from '../componentes/Icono';
import { Ventana } from '../componentes/Ventana';
import { dinero } from '../logica/dinero';
import { haySiguiente, usePaginacionCursor } from '../logica/paginacion';
import { Contador } from '../componentes/Contador';
import { diaLargo, horaEn, hoyEn } from '../pantallas/reservas/fechas';
import { AvisoPc, BotonPc, Pagina } from './Pagina';
import './reserva.css';

/**
 * 8.12 «Mis reservas» (`/mis-reservas`, Cuenta Reservaria: fondo negro con la ola y el logo de Reservaria): con el token
 * de cliente de «Mis datos» (2.9), el negocio entre sus perfiles (`listMyBusinessProfiles`) y sus reservas ahí
 * (`listMyReservations`, Próximas · Pasadas). En cada una: localizador, cuándo, dónde, personas, lo pagado, la tarjeta
 * guardada y lo devuelto (G797) y hasta cuándo cancela gratis. Cambiar fecha, hora o personas (nunca la zona ni el recurso,
 * G19) solo si `allowed_actions` trae `change` (`changeMyReservation` con `If-Match`; le llega un pase nuevo y el anterior
 * deja de valer, G466); si no, «Ya ha pasado el plazo…». Cancelar con lo que le corresponde
 * (`getMyReservationCancellationTerms` → `cancelMyReservation` con `expected_outcome`), también con el negocio en solo
 * lectura (G55). «Ver mi pase» lleva a «Mis pases» (6.7). Con `pay` en `allowed_actions`, «Pagar X €» abre `payment_url` con `amount_due` (G881, G908).
 */
const ESTADO: Readonly<Record<MiReserva['status'], readonly [string, EstadoChip]>> = {
    pending_payment: ['Falta pagar', 'pending'],
    confirmed: ['Confirmada', 'confirmed'],
    arrived: ['Dentro', 'live'],
    pending_completion: ['Pasada', 'neutral'],
    completed: ['Pasada', 'neutral'],
    no_show: ['No fuiste', 'problem'],
    cancelled: ['Cancelada', 'neutral'],
    expired: ['Caducada', 'neutral'],
    refunded: ['Devuelta', 'neutral'],
};

const DINERO: Readonly<Record<CondicionesMiCancelacion['outcome'], string>> = {
    full_refund: 'Te devolvemos todo lo pagado',
    partial_refund: 'Te devolvemos una parte',
    retain: 'No se devuelve lo pagado',
    late_fee: 'Se cobra la cancelación tardía',
    nothing_to_pay: 'No has pagado nada',
};

interface Props {
    ops: OperacionesClienteFinal;
    reservas: OperacionesMisReservas;
    navegar: (ruta: string) => void;
    onSalir: () => void;
    onCaducada: () => void;
    movil?: boolean;
}

export function MisReservas({ ops, reservas, navegar, onSalir, onCaducada, movil = true }: Props) {
    const [perfiles, setPerfiles] = useState<MiPerfil[] | null>(null);
    const [error, setError] = useState(false);
    const [elegido, setElegido] = useState<string | null>(null);
    const [cuando, setCuando] = useState<'upcoming' | 'past'>('upcoming');
    const [lista, setLista] = useState<{ clave: string; datos: ListaMisReservas | null; error: boolean } | null>(null);
    const [abierta, setAbierta] = useState<string | null>(null);
    const [cambio, setCambio] = useState<{ id: string; dia: string; hora: string; personas: number } | null>(null);
    const [cancelar, setCancelar] = useState<{ reserva: MiReserva; condiciones: CondicionesMiCancelacion | null } | null>(null);
    const [ocupado, setOcupado] = useState(false);
    const [aviso, setAviso] = useState<{ texto: string; tipo: 'error' | 'info' } | null>(null);
    const [intento, setIntento] = useState(0);
    const pag = usePaginacionCursor();
    const { reiniciar } = pag;

    const tratar = (e: unknown, texto: string) => {
        if (e instanceof ErrorApi && e.estado === 401) {
            onCaducada();
        } else {
            setAviso({ tipo: 'error', texto });
        }
    };

    useEffect(() => {
        let vivo = true;
        ops.listMyBusinessProfiles({ limit: 100 })
            .then((l) => { if (vivo) { setPerfiles(l.data.filter((p) => p.ended == null)); setError(false); } })
            .catch((e: unknown) => {
                if (!vivo) {
                    return;
                }
                if (e instanceof ErrorApi && e.estado === 401) {
                    onCaducada();
                } else {
                    setError(true);
                }
            });
        return () => { vivo = false; };
        // onCaducada cambia en cada pintado: solo se mira al cargar.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, intento]);

    const perfil = perfiles?.find((p) => p.customer_id === elegido) ?? perfiles?.[0] ?? null;
    const idPerfil = perfil?.customer_id ?? null;
    const clave = `${idPerfil ?? ''}|${cuando}|${pag.cursor ?? ''}|${String(intento)}`;
    useEffect(() => {
        if (idPerfil === null) {
            return;
        }
        let vivo = true;
        reservas.listMyReservations(idPerfil, cuando, { cursor: pag.cursor, limit: 20 })
            .then((datos) => { if (vivo) { setLista({ clave, datos, error: false }); } })
            .catch((e: unknown) => {
                if (!vivo) {
                    return;
                }
                if (e instanceof ErrorApi && e.estado === 401) {
                    onCaducada();
                } else {
                    setLista({ clave, datos: null, error: true });
                }
            });
        return () => { vivo = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reservas, idPerfil, cuando, pag.cursor, clave]);

    const reintentar = () => { setIntento((i) => i + 1); };
    const nombre = perfil?.business.name ?? '';

    if (error) {
        return (
            <Pagina cabecera={{ tipo: 'cuenta' }} pie={{ tipo: 'cuenta' }} onSalir={onSalir} centrada>
                <section className="rv-pc-card">
                    <div className="rv-pc-card__cab"><h1>No hemos podido cargar tus reservas</h1><span>Comprueba la conexión y prueba de nuevo.</span></div>
                    <BotonPc texto="Reintentar" icono="refresh-cw" onClick={reintentar} />
                </section>
            </Pagina>
        );
    }

    const actual = lista?.clave === clave ? lista : null;
    const filas = actual?.datos?.data ?? null;
    const cargando = perfiles === null || (perfil !== null && actual === null);

    const guardarCambio = (r: MiReserva) => {
        if (cambio === null || idPerfil === null) {
            return;
        }
        setOcupado(true);
        setAviso(null);
        reservas.changeMyReservation(idPerfil, r, { when: { local_date: cambio.dia, local_time: cambio.hora }, party_size: cambio.personas })
            .then((x) => {
                setCambio(null);
                setAviso({ tipo: 'info', texto: x.next_action.kind === 'pay' ? 'Reserva cambiada. Falta pagar la diferencia: te lo hemos enviado.' : 'Reserva cambiada. Te llega la confirmación y un pase nuevo; el anterior ya no vale.' });
                reintentar();
            })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.estado === 409) {
                    setAviso({ tipo: 'error', texto: 'A esa hora no hay sitio. Prueba con otra.' });
                } else if (e instanceof ErrorApi && e.estado === 412) {
                    setAviso({ tipo: 'error', texto: 'La reserva ha cambiado mientras la mirabas. Vuelve a abrirla.' });
                    reintentar();
                } else {
                    tratar(e, 'No hemos podido cambiar la reserva. Prueba de nuevo.');
                }
            })
            .finally(() => { setOcupado(false); });
    };

    const abrirCancelar = (r: MiReserva) => {
        if (idPerfil === null) {
            return;
        }
        setAviso(null);
        setCancelar({ reserva: r, condiciones: null });
        reservas.getMyReservationCancellationTerms(idPerfil, r.id)
            .then((c) => { setCancelar((x) => (x === null ? null : { ...x, condiciones: c })); })
            .catch((e: unknown) => { setCancelar(null); tratar(e, 'No hemos podido ver qué pasa si cancelas. Prueba de nuevo.'); });
    };

    const confirmarCancelar = () => {
        if (cancelar?.condiciones == null || idPerfil === null) {
            return;
        }
        setOcupado(true);
        reservas.cancelMyReservation(idPerfil, cancelar.reserva.id, { expected_outcome: cancelar.condiciones.outcome })
            .then(() => { setCancelar(null); setAviso({ tipo: 'info', texto: 'Reserva cancelada. Te lo confirmamos por tu canal de contacto.' }); reintentar(); })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.estado === 409) {
                    setAviso({ tipo: 'error', texto: 'Las condiciones han cambiado. Vuelve a mirar qué pasa si cancelas.' });
                    setCancelar(null);
                } else {
                    setCancelar(null);
                    tratar(e, 'No hemos podido cancelar. Prueba de nuevo.');
                }
            })
            .finally(() => { setOcupado(false); });
    };

    return (
        <Pagina
            cabecera={{ tipo: 'cuenta' }}
            pie={{ tipo: 'cuenta' }}
            onSalir={onSalir}
            titulo={{ titulo: 'Mis reservas', sub: nombre === '' ? 'Tus reservas, tus pases y lo que cambie, desde aquí.' : `Tus reservas en ${nombre}.` }}
        >
            {perfiles !== null && perfiles.length > 1 && (
                <label className="rv-pc-negocio__canal">
                    <span>Negocio</span>
                    <select className="rv-pc-select rv-pc-select--ancho" value={perfil?.customer_id ?? ''} onChange={(e) => { setElegido(e.target.value); setAbierta(null); reiniciar(); }}>
                        {perfiles.map((p) => <option key={p.customer_id} value={p.customer_id}>{p.business.name}</option>)}
                    </select>
                </label>
            )}
            <div className="rv-pc-paginas" role="tablist" aria-label="Cuándo">
                {(['upcoming', 'past'] as const).map((c) => (
                    <button key={c} type="button" role="tab" aria-selected={cuando === c} className={`rv-pc-b rv-pc-b--peq ${cuando === c ? 'rv-pc-b--pri' : 'rv-pc-b--sec'}`} onClick={() => { setCuando(c); setAbierta(null); reiniciar(); }}>
                        <span>{c === 'upcoming' ? 'Próximas' : 'Pasadas'}</span>
                    </button>
                ))}
            </div>
            {aviso !== null && <AvisoPc texto={aviso.texto} tipo={aviso.tipo} />}
            <section className="rv-pc-card rv-pc-card--seccion" aria-label="Tus reservas" aria-busy={cargando}>
                {cargando ? (
                    <>
                        <div className="rv-pc-esqueleto" style={{ height: 56 }} />
                        <div className="rv-pc-esqueleto" style={{ height: 56, marginTop: 12 }} />
                    </>
                ) : actual?.error === true ? (
                    <div className="rv-pc-vacio">
                        <span><Icono nombre="circle-alert" /></span>
                        <strong>No hemos podido cargar tus reservas</strong>
                        <BotonPc texto="Reintentar" icono="refresh-cw" tipo="sec" peq onClick={reintentar} />
                    </div>
                ) : filas === null || filas.length === 0 ? (
                    <div className="rv-pc-vacio">
                        <span><Icono nombre="calendar-check" /></span>
                        <strong>{cuando === 'upcoming' ? 'No tienes reservas próximas' : 'No tienes reservas pasadas'}</strong>
                        <small>Cuando reserves aquí, las verás con tus pases.</small>
                    </div>
                ) : (
                    filas.map((r) => {
                        const [texto, chip] = ESTADO[r.status];
                        const id = `rv-mr-${r.id}`;
                        const viva = ['pending_payment', 'confirmed'].includes(r.status);
                        const puedeCambiar = r.allowed_actions.includes('change');
                        const enCambio = cambio?.id === r.id ? cambio : null;
                        return (
                            <div key={r.id} className="rv-pc-pago">
                                <button type="button" className="rv-pc-pago__cab" aria-expanded={abierta === r.id} aria-controls={id} onClick={() => { setAbierta((a) => (a === r.id ? null : r.id)); setCambio(null); }}>
                                    <span className="rv-pc-pago__icono" aria-hidden="true"><Icono nombre="calendar-check" /></span>
                                    <span>
                                        <strong>{r.zone_public_name}</strong>
                                        <small>{`${diaLargo(r.local_date)}${r.local_time === undefined ? '' : `, ${r.local_time}`} · ${String(r.party_size)} personas`}</small>
                                    </span>
                                    <span className="rv-pc-pago__importe">{r.status !== 'confirmed' && <Chip estado={chip}>{texto}</Chip>}</span>
                                    <Icono nombre="chevron-down" />
                                </button>
                                {abierta === r.id && (
                                    <div id={id} className="rv-pc-pago__cuerpo rv-mr-fila">
                                        <div className="rv-pc-dato"><span>Localizador</span><strong>{r.reference}</strong></div>
                                        <div className="rv-pc-dato"><span>Cuándo</span><strong>{`${diaLargo(r.local_date)}${r.local_time === undefined ? '' : `, ${r.local_time}`}${r.check_out_date == null ? '' : ` a ${diaLargo(r.check_out_date)}`}`}</strong></div>
                                        <div className="rv-pc-dato"><span>Dónde</span><strong>{[r.zone_public_name, r.resource_public_name ?? ''].filter((x) => x !== '').join(' · ')}</strong></div>
                                        <div className="rv-pc-dato"><span>Personas</span><strong>{r.party_size}</strong></div>
                                        {r.event != null && <div className="rv-pc-dato"><span>Evento</span><strong>{r.event.public_name}</strong></div>}
                                        {r.total !== undefined && <div className="rv-pc-dato"><span>Total</span><strong>{dinero(r.total)}</strong></div>}
                                        {r.amount_paid != null && <div className="rv-pc-dato"><span>Pagado</span><strong>{dinero(r.amount_paid)}</strong></div>}
                                        {r.card_saved === true && <div className="rv-pc-dato"><span>Garantía</span><strong>Tarjeta guardada</strong></div>}
                                        {r.amount_refunded != null && <div className="rv-pc-dato"><span>Devuelto</span><strong>{dinero(r.amount_refunded)}</strong></div>}
                                        {r.free_cancellation_until != null && <div className="rv-pc-dato"><span>Cancelación gratis hasta</span><strong>{`${diaLargo(hoyEn(r.timezone, new Date(r.free_cancellation_until)))}, ${horaEn(r.free_cancellation_until, r.timezone)}`}</strong></div>}
                                        {/* G881, G908: «Pagar X €» lo pendiente con el enlace y la cifra de la API (no se resta aquí), solo con la acción `pay`. */}
                                        {r.allowed_actions.includes('pay') && r.payment_url != null && (
                                            <a className="rv-pc-b rv-pc-b--pri" href={r.payment_url}><Icono nombre="lock" /><span>{r.amount_due == null ? 'Pagar' : `Pagar ${dinero(r.amount_due)}`}</span></a>
                                        )}
                                        {viva && <BotonPc texto="Ver mi pase" icono="qr-code" tipo="sec" onClick={() => { navegar('/mis-pases'); }} />}
                                        {viva && !puedeCambiar && <p className="rv-pc-nota"><Icono nombre="info" /><span>Ya ha pasado el plazo para cambiarla desde aquí. Si necesitas algo, escribe al negocio.</span></p>}
                                        {viva && puedeCambiar && enCambio === null && (
                                            <BotonPc texto="Cambiar fecha, hora o personas" icono="pencil" tipo="sec" onClick={() => { setAviso(null); setCambio({ id: r.id, dia: r.local_date, hora: r.local_time ?? '', personas: r.party_size }); }} />
                                        )}
                                        {enCambio !== null && (
                                            <div className="rv-mr-cambio">
                                                <p className="rv-pc-nota"><Icono nombre="info" /><span>Puedes cambiar la fecha, la hora o las personas. Te llegará un pase nuevo y el anterior dejará de valer.</span></p>
                                                <Fecha etiqueta="Día" valor={enCambio.dia} onChange={(v) => { setCambio({ ...enCambio, dia: v }); }} />
                                                <Campo etiqueta="Hora (HH:MM)" valor={enCambio.hora} onChange={(v) => { setCambio({ ...enCambio, hora: v }); }} max={5} />
                                                <Contador etiqueta="Personas" ayuda="" valor={enCambio.personas} onCambio={(n) => { setCambio({ ...enCambio, personas: n }); }} />
                                                <BotonPc texto="Guardar el cambio" icono="check" ocupado={ocupado} ocupadoTexto="Cambiando…" onClick={() => { guardarCambio(r); }} />
                                                <BotonPc texto="No cambiar" icono="x" tipo="sec" onClick={() => { setCambio(null); }} />
                                            </div>
                                        )}
                                        {r.allowed_actions.includes('cancel') && <BotonPc texto="Cancelar la reserva" icono="calendar-x" tipo="sec" rojo onClick={() => { abrirCancelar(r); }} />}
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </section>
            {actual?.datos != null && (pag.hayAnterior || haySiguiente(actual.datos.next_cursor)) && (
                <div className="rv-pc-paginas">
                    <BotonPc texto="Anterior" icono="arrow-left" tipo="sec" peq desactivado={!pag.hayAnterior} onClick={pag.anterior} />
                    <BotonPc texto="Siguiente" icono="arrow-right" tipo="sec" peq desactivado={!haySiguiente(actual.datos.next_cursor)} onClick={() => { pag.siguiente(actual.datos?.next_cursor ?? null); }} />
                </div>
            )}
            <Ventana
                abierta={cancelar !== null}
                titulo="¿Cancelar la reserva?"
                texto={cancelar?.condiciones == null ? 'Mirando qué pasa con lo pagado…' : `${DINERO[cancelar.condiciones.outcome]}${cancelar.condiciones.refund_amount === undefined ? '' : ` (${dinero(cancelar.condiciones.refund_amount)})`}.${cancelar.condiciones.terms_text === undefined ? '' : ` ${cancelar.condiciones.terms_text}`}`}
                cancelar="Mantener"
                accion="Cancelar la reserva"
                accionIcono="calendar-x"
                ocupado={ocupado}
                ocupadoTexto="Cancelando…"
                inactiva={cancelar?.condiciones == null || !cancelar.condiciones.can_cancel}
                movil={movil}
                onAccion={confirmarCancelar}
                onCerrar={() => { setCancelar(null); }}
            />
        </Pagina>
    );
}
